import * as React from 'react';
import { Component } from 'react';
import * as PropTypes from 'prop-types';
import { mileageToString, UNLIMITED_MILEAGE } from '@makemydeal/dr-platform-shared';

import RadioButtonGroupUI from '../../components/RadioButtonGroup/RadioButtonGroupUI';
const RadioButtonGroup = RadioButtonGroupUI;

import ProductPackageCardGroupUI from '../../components/ProductPackageCardGroup/ProductPackageCardGroupUI';
const ProductPackageCardGroup = ProductPackageCardGroupUI;

import { IMenuCardPlan } from './types';

import { Parser } from 'bbcode-to-react';
const parser = new Parser();

export interface IMapUnlimitedToMiles{
    Unlimited: number | null;
}

export interface IMenuProductDetailsStateProps {
    fullDetails?: string;
    isShowDetailsOpen?: boolean;
    isRated?: boolean;
    cardId?: string;
    plans: IMenuCardPlan[];
    mileages: number[];
    terms: number[];
    selectedTerm: number;
    selectedMileage: number;
    selectedDeductible: number;
    errorMessage: string;
    mapUnlimitedToMiles?: IMapUnlimitedToMiles;
    coverageName?: string;
}

export interface IMenuProductDetailsDispatchProps {
    onTermChange: { (term: number) };
    onMileageChange: { (mileage: number) };
}

export interface IMenuProductDetailsProps extends IMenuProductDetailsStateProps, IMenuProductDetailsDispatchProps {
}

export interface IMenuProductDetailsState {
}

export default class MenuProductDetails extends Component<IMenuProductDetailsProps, IMenuProductDetailsState> {
    handleTermChange(termValue: string) {
        if (this.props.onTermChange) {
            this.props.onTermChange(this.stringToTerm(termValue));
        }
    }
    handleMileageChange(mileageValue: string) {
        if (this.props.onMileageChange) {
            this.props.onMileageChange(this.stringToMileage(mileageValue));
        }
    }

    mileageToStringWithProps(mileage: number): string {
        const mileageToStringResult = mileageToString(mileage);
        if (mileageToStringResult === UNLIMITED_MILEAGE) {
            this.props.mapUnlimitedToMiles[UNLIMITED_MILEAGE] = mileage;
        }
        return mileageToStringResult;
    }

    stringToMileage(value: string): number {
        let mileage: number = undefined;
        const unlimitedStrKey = 'Unlimited';

        if (value && value.length > 0) {
            const end = value.substr(value.length - 1, 1);
            const prefix = value.substr(0, value.length - 1);
            if (value === unlimitedStrKey) {
                mileage = this.props.mapUnlimitedToMiles[unlimitedStrKey];
            }
            if (end === 'k') {
                mileage = parseInt(`${prefix}000`, 10);
            }
        }
        return mileage;
    }

    stringToTerm(value: string): number {
        return parseInt(value, 10);
    }

    termToString(term: number): string {
        if (term) {
            return term.toString();
        }
    }

    deductibleToString(deductible: number): string {
        // allow for zero deductible to pass logic check
        if (deductible !== null && deductible !== undefined) {
            return deductible.toString();
        }
    }

    buildMileageList(mileages: number[]): string[] {
        return mileages.map(mileage => this.mileageToStringWithProps(mileage));
    }

    buildTermList(terms: number[]): string[] {
        return terms.map(term => this.termToString(term));
    }

    buildDeductibleList(deductibles: number[]): string[] {
        this.props.selectedDeductible;
        return deductibles.map(deductible => `$${this.deductibleToString(deductible)}`);
    }

    getParsedFullDescription(fullDetails: string) {
        let parsedFullDescription;
        try {
            const convertedBBCode = [];
            const fullDetailsArray = JSON.parse(fullDetails);
            fullDetailsArray.forEach(arrayItem => {
                convertedBBCode.push(parser.toReact(arrayItem));
            });
            parsedFullDescription = convertedBBCode;
        } catch (e) {
            parsedFullDescription = fullDetails;
        }
        return parsedFullDescription;
    }

    showCoverageNameIfExists() {
        const { coverageName } = this.props;
        if (coverageName) {
            return(
                <div className="coverage-name-group">
                    <div className="button-group-label">Coverage Name</div>
                    <div className="coverage-name">
                        <strong>{coverageName}</strong>
                    </div>
                </div>
            );
        }
        return null;
    }

    showDeductibleIfExists() {
        const { selectedDeductible, cardId } = this.props;
        if (selectedDeductible !== null) {
            // If data allows user to select from mutiple deductibles,
            // this array should be passed in via props just like miles.
            const deductibles = [selectedDeductible];
            const deductibleArray = this.buildDeductibleList(deductibles);
            return (
                <div className="deductible-group">
                    <div className="button-group-label">Deductible</div>
                    <RadioButtonGroup
                        itemArray={deductibleArray}
                        name="deductible"
                        cardId={cardId}
                        selectedItem={null}
                    />
                </div>
            );
        }
        return null;
    }

    render() {
        const {
            errorMessage,
            fullDetails,
            isShowDetailsOpen,
            isRated,
            cardId,
            mileages,
            plans,
            terms,
            selectedTerm,
            selectedMileage,
            selectedDeductible,
            coverageName
        } = this.props;

        if (isShowDetailsOpen) {
            const errorMessageContent = !errorMessage ? [] : (
                <div className="error">
                    {errorMessage}
                </div>
            );
            let ratedDetails;
            if (isRated) {
                // '65k', '75k', '85k', '100k', '125k'
                const milesArray = this.buildMileageList(mileages);
                // [36, 48, 60, 72, 84]
                const termsArray = this.buildTermList(terms);

                ratedDetails = (
                    <div>
                        <div className={`${termsArray.length === 0 ? 'hidden' : 'terms-mileage-group'}`}>
                            <div className="header">Select Your Coverage Options</div>
                            <div className="coverage-detail-container">
                                {this.showCoverageNameIfExists()}
                                <div className="button-group-container">
                                    <div className="months-group">
                                        <div className="button-group-label">Months</div>
                                        <RadioButtonGroup
                                            itemArray={termsArray}
                                            name="term"
                                            cardId={cardId}
                                            selectedItem={this.termToString(selectedTerm)}
                                            onChange={(value) => { this.handleTermChange(value); }}
                                        />
                                    </div>
                                    <div className="miles-group">
                                        <div className="button-group-label">Miles</div>
                                        <RadioButtonGroup
                                            itemArray={milesArray}
                                            name="mileage"
                                            cardId={cardId}
                                            selectedItem={this.mileageToStringWithProps(selectedMileage)}
                                            onChange={(value) => { this.handleMileageChange(value); }}
                                        />
                                    </div>
                                    {this.showDeductibleIfExists()}
                                </div>
                            </div>
                        </div>
                        {/*
                            The following code is commented out until a story is created to re-build this functionality.
                        */}
                        {/* <div className={`${termsArray.length === 0 ? 'hidden' : ''}`}>
                            <ProductPackageCardGroup
                                cardId={cardId}
                                plans={plans}
                                onChange={(value) => { this.handlePlanChange(value); }}
                            />
                        </div> */}
                    </div>
                );
            }
            else {
                ratedDetails = [];
            }
            return (
                <div>
                    {errorMessageContent}
                    {ratedDetails}
                    {this.getParsedFullDescription(fullDetails)}
                </div>
            );
        }

        return null;
    }
}
